import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ActionSelect } from '../../Action'
import { BUTTON_HEIGHT_24 } from '../../Button'
import { BirdviewCard } from '../BirdviewCard'
import {
  BIRDVIEW_CHART_DEFAULT_PERIOD,
  BirdviewChartAction,
} from '../DashboardBirdview.constants.charts'
import {
  QA_NAME_DASHBOARD_BIRDVIEW_CHART_ACTIONS,
  QA_NAME_DASHBOARD_BIRDVIEW_CHART_METRIC,
  QA_NAME_DASHBOARD_BIRDVIEW_CHART_PERIOD,
} from '../DashboardBirdview.constants.qa'
import { BIRDVIEW_SECTION_TARGET } from '../DashboardBirdview.constants.targets'
import { useBirdviewListTarget } from '../DashboardBirdview.hooks'

import {
  getBirdviewChartActions,
  getBirdviewChartContent,
  getBirdviewChartControls,
  getBirdviewNextChartOptionValue,
} from './BirdviewChartCard.utils'
import { getBirdviewChartPeriodData } from './BirdviewChartCard.utils.data'
import { BirdviewChartControlSelect } from './BirdviewChartControlSelect'

import * as styles from './BirdviewChartCard.module.css'

function BirdviewChartCard({
  chart,
  dragHandleProps,
  section,
  title,
  onCycleSize,
  onNavigate,
  onRemove,
}) {
  const { t } = useTranslation()

  const [periodValue, setPeriodValue] = useState(
    BIRDVIEW_CHART_DEFAULT_PERIOD.value
  )
  const [metricValue, setMetricValue] = useState(chart.metricOptions?.[0].value)

  const { getListTarget } = useBirdviewListTarget()

  const { metric, metricOptions, period, periodOptions } =
    getBirdviewChartControls({ chart, metricValue, periodValue, t })
  const data = getBirdviewChartPeriodData({
    chart,
    metric: metricValue,
    period,
    section,
  })

  function handleSelectPeriod(option) {
    setPeriodValue(option.value)
  }

  function handleSelectMetric(option) {
    setMetricValue(option.value)
  }

  function handleClickAction({ value }) {
    switch (value) {
      case BirdviewChartAction.Period:
        setPeriodValue(
          getBirdviewNextChartOptionValue(periodOptions, periodValue)
        )
        break
      case BirdviewChartAction.Size:
        onCycleSize(chart.id)
        break
      case BirdviewChartAction.Open:
        onNavigate(getListTarget(BIRDVIEW_SECTION_TARGET.requests))
        break
      default:
        onRemove(chart.id)
    }
  }

  return (
    <BirdviewCard
      dragHandleProps={dragHandleProps}
      headerMenu={
        <ActionSelect
          actions={getBirdviewChartActions(t)}
          controlProps={{
            buttonProps: {
              height: BUTTON_HEIGHT_24,
              qaName: QA_NAME_DASHBOARD_BIRDVIEW_CHART_ACTIONS,
            },
          }}
          onClickAction={handleClickAction}
        />
      }
      state={section.state}
      title={title}
      onRetry={section.reload}
    >
      <div className={styles.body}>
        <div className={styles.controls}>
          <BirdviewChartControlSelect
            options={periodOptions}
            qaName={QA_NAME_DASHBOARD_BIRDVIEW_CHART_PERIOD}
            value={period}
            onSelect={handleSelectPeriod}
          />
          {metric ? (
            <BirdviewChartControlSelect
              options={metricOptions}
              qaName={QA_NAME_DASHBOARD_BIRDVIEW_CHART_METRIC}
              value={metric}
              onSelect={handleSelectMetric}
            />
          ) : null}
        </div>
        <div className={styles.chart}>
          {getBirdviewChartContent({ chart, data, metric: metricValue, t })}
        </div>
      </div>
    </BirdviewCard>
  )
}

export default BirdviewChartCard
